/**
 * Los dos campos de un rango de fechas: DESDE, HASTA y el botón de limpiar.
 *
 * Vive aparte porque tiene DOS anfitriones. Uno es el desplegable de
 * `DateRangePicker` —el botón que escribe el rango, el de las barras de
 * filtros—; el otro es el filtro que cuelga de la cabecera de una columna
 * (`CabeceraFiltrable`). Si el marcado siguiera dentro del primero, el segundo
 * tendría que abrir un desplegable DENTRO de otro desplegable para
 * reaprovecharlo, con dos cierres por clic fuera peleándose por el mismo clic.
 *
 * No decide nada: ni dónde se pinta, ni cuándo se abre, ni qué significa el
 * rango. Sólo son dos `input` y un botón.
 */
import { useTranslation } from 'react-i18next'

interface Props {
  dark: boolean
  desde: string
  hasta: string
  onDesde: (v: string) => void
  onHasta: (v: string) => void
  /** El botón de limpiar. Sin esta prop no se pinta: hay anfitriones que ya
   *  tienen el suyo en un pie común y tendrían dos. */
  onLimpiar?: () => void
}

export function CamposRango({ dark, desde, hasta, onDesde, onHasta, onLimpiar }: Props) {
  const { t } = useTranslation()

  const labelCls = `text-[10px] font-semibold uppercase tracking-wide ${
    dark ? 'text-slate-400' : 'text-slate-500'
  }`
  const inputCls = [
    'text-[12px] px-2.5 py-1.5 rounded-lg border outline-none transition-colors w-full',
    dark
      ? 'bg-slate-700 border-white/10 text-slate-100 placeholder-slate-500 focus:border-sky-500/60'
      : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:border-sky-400 shadow-sm',
  ].join(' ')

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className={`block mb-1 ${labelCls}`}>{t('common.from')}</label>
        <input
          type="date"
          value={desde}
          onChange={e => onDesde(e.target.value)}
          className={inputCls}
        />
      </div>
      <div>
        <label className={`block mb-1 ${labelCls}`}>{t('common.to')}</label>
        <input
          type="date"
          value={hasta}
          onChange={e => onHasta(e.target.value)}
          className={inputCls}
        />
      </div>
      {onLimpiar && (desde || hasta) && (
        <button
          type="button"
          onClick={onLimpiar}
          className={`text-[11px] font-semibold text-center w-full py-1.5 rounded-lg transition-colors border cursor-pointer ${
            dark
              ? 'text-rose-400 border-rose-500/20 hover:bg-rose-500/10'
              : 'text-rose-500 border-rose-200 hover:bg-rose-50'
          }`}
        >
          {t('common.clearFilters')}
        </button>
      )}
    </div>
  )
}
